import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../lib/auth";
import { useSEO } from "../lib/useSEO";
import { Sparkles, Upload, Trash2, Compass } from "lucide-react";
import AdminNav from "../components/AdminNav";

const empty = {
  name: "",
  country: "",
  state: "",
  city: "",
  address: "",
  description: "",
  short_blurb: "",
  safety_notes: "",
  lat: 0,
  lng: 0,
  height_m: 0,
  youtube_url: "",
  image_urls: [],
  meta_title: "",
  meta_description: "",
  featured: false,
  is_draft: false,
};

export default function AdminEditWaterfall() {
  const { id } = useParams();
  const isNew = id === "new";
  const { user, loading: authLoading } = useAuth();
  const nav = useNavigate();
  const [data, setData] = useState(empty);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [genBusy, setGenBusy] = useState(false);
  const [enrichBusy, setEnrichBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");

  useSEO({ title: `${isNew ? "New" : "Edit"} Waterfall | Admin` });

  useEffect(() => {
    if (isNew || !user) return;
    (async () => {
      const r = await api.get("/admin/waterfalls", { params: { limit: 1 } });
      // Use list endpoint to find by id, simpler: fetch all and pick. But we have /waterfalls/{slug}. We use admin list with id filter not supported.
      // Instead, fetch via slug? We have id. Just fetch via /waterfalls/ if slug available.
      // Simpler: fetch by listing all
      const all = await api.get("/admin/waterfalls", { params: { limit: 2000 } });
      const found = all.data.items.find((w) => w.id === id);
      if (found) setData(found);
      setLoading(false);
    })();
  }, [id, isNew, user]);

  if (!authLoading && !user) return <Navigate to="/admin/login" replace />;

  const update = (k, v) => setData((d) => ({ ...d, [k]: v }));

  const save = async () => {
    setSaving(true);
    setMsg("");
    try {
      if (isNew) {
        const r = await api.post("/admin/waterfalls", data);
        nav(`/admin/waterfalls/${r.data.id}`);
      } else {
        await api.put(`/admin/waterfalls/${id}`, data);
        setMsg("Saved!");
      }
    } catch (e) {
      setMsg("Error: " + (e.response?.data?.detail || e.message));
    } finally {
      setSaving(false);
    }
  };

  const generateAI = async () => {
    if (isNew) {
      setMsg("Save first, then generate AI content.");
      return;
    }
    setGenBusy(true);
    setEnrichBusy(true);
    setMsg("");
    try {
      // Run both AI passes in parallel so a single click populates
      // description/safety AND the full visit-info (parking, hike, dogs, swim…)
      const [descRes, visitRes] = await Promise.allSettled([
        api.post(`/admin/waterfalls/${id}/generate`),
        api.post(`/admin/waterfalls/${id}/enrich`),
      ]);
      const updates = {};
      if (descRes.status === "fulfilled") {
        Object.assign(updates, {
          description: descRes.value.data.description,
          safety_notes: descRes.value.data.safety_notes,
          meta_title: descRes.value.data.meta_title,
          meta_description: descRes.value.data.meta_description,
          address: descRes.value.data.address,
        });
      }
      if (visitRes.status === "fulfilled") {
        Object.assign(updates, {
          swimming_allowed: visitRes.value.data.swimming_allowed,
          dog_rules: visitRes.value.data.dog_rules,
          official_website: visitRes.value.data.official_website,
          best_time_to_visit: visitRes.value.data.best_time_to_visit,
          nearby_attractions: visitRes.value.data.nearby_attractions,
          parking_info: visitRes.value.data.parking_info,
          hike_length: visitRes.value.data.hike_length,
          hike_difficulty: visitRes.value.data.hike_difficulty,
          entry_fee: visitRes.value.data.entry_fee,
          trail_notes: visitRes.value.data.trail_notes,
          why_famous: visitRes.value.data.why_famous,
        });
      }
      setData((d) => ({ ...d, ...updates }));
      const errs = [descRes, visitRes].filter((r) => r.status === "rejected").map((r) => r.reason?.response?.data?.detail || r.reason?.message || "unknown");
      setMsg(errs.length ? `Partial success — ${errs.join(" / ")}` : "AI content + visit info generated.");
    } catch (e) {
      setMsg("AI failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setGenBusy(false);
      setEnrichBusy(false);
    }
  };

  // Kept as a separate action in case admins want to only refresh the visit info
  const enrichVisitInfo = async () => {
    if (isNew) {
      setMsg("Save first, then generate visit info.");
      return;
    }
    setEnrichBusy(true);
    setMsg("");
    try {
      const r = await api.post(`/admin/waterfalls/${id}/enrich`);
      setData((d) => ({
        ...d,
        swimming_allowed: r.data.swimming_allowed,
        dog_rules: r.data.dog_rules,
        official_website: r.data.official_website,
        best_time_to_visit: r.data.best_time_to_visit,
        nearby_attractions: r.data.nearby_attractions,
        parking_info: r.data.parking_info,
        hike_length: r.data.hike_length,
        hike_difficulty: r.data.hike_difficulty,
        entry_fee: r.data.entry_fee,
        trail_notes: r.data.trail_notes,
        why_famous: r.data.why_famous,
      }));
      setMsg("Visit info generated.");
    } catch (e) {
      setMsg("Enrich failed: " + (e.response?.data?.detail || e.message));
    } finally {
      setEnrichBusy(false);
    }
  };

  const uploadImage = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      // Store the RELATIVE URL ('/api/files/{id}') so it resolves to whatever
      // host the page is served from (preview or production). Avoids hardcoding
      // the current hostname into the saved record.
      update("image_urls", [...(data.image_urls || []), r.data.url]);
    } catch (err) {
      setMsg("Upload failed: " + err.message);
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const removeImage = (i) => {
    update("image_urls", data.image_urls.filter((_, idx) => idx !== i));
  };

  if (loading) return <main className="p-8">Loading…</main>;

  return (
    <main className="min-h-screen bg-stone-50">
      <AdminNav />
      <div className="bg-white border-b border-stone-200">
        <div className="max-w-5xl mx-auto px-4 py-3 flex items-center justify-end gap-3 flex-wrap">
            <button
              onClick={generateAI}
              disabled={genBusy || isNew}
              data-testid="edit-generate-ai-button"
              className="btn-pill bg-[hsl(var(--accent))] text-white text-sm py-2 disabled:opacity-50"
              title="Runs both description/safety and visit-info (parking, hike, dogs, swim) generators"
            >
              <Sparkles className="w-4 h-4 mr-1" />
              {genBusy ? "Generating…" : "Generate all AI content"}
            </button>
            <button
              onClick={enrichVisitInfo}
              disabled={enrichBusy || isNew}
              data-testid="edit-enrich-button"
              className="btn-pill bg-emerald-700 text-white text-sm py-2 disabled:opacity-50"
              title="Regenerate only the visit-info (parking / hike / dogs / swim / best time / etc)"
            >
              <Compass className="w-4 h-4 mr-1" />
              {enrichBusy ? "Regenerating…" : "Visit info only"}
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="edit-save-button"
              className="btn-pill bg-[hsl(var(--primary))] text-white text-sm py-2 disabled:opacity-50"
            >
              {saving ? "Saving…" : "Save"}
            </button>
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4 py-8 space-y-6">
        <h1 className="font-display text-3xl font-bold text-stone-900">{isNew ? "New waterfall" : data.name}</h1>
        {msg && <p className="text-sm text-stone-700 bg-stone-100 inline-block px-3 py-1 rounded-full">{msg}</p>}

        <div className="rounded-2xl bg-white border border-stone-200 p-6 space-y-4">
          <h2 className="font-display font-semibold">Basics</h2>
          <Grid>
            <Field label="Name" value={data.name} onChange={(v) => update("name", v)} testid="edit-name" />
            <Field label="Country" value={data.country} onChange={(v) => update("country", v)} testid="edit-country" />
            <Field label="State / Region" value={data.state} onChange={(v) => update("state", v)} testid="edit-state" />
            <Field label="City / Area" value={data.city} onChange={(v) => update("city", v)} testid="edit-city" />
            <Field label="Address" value={data.address} onChange={(v) => update("address", v)} testid="edit-address" wide />
            <Field label="Latitude" type="number" value={data.lat} onChange={(v) => update("lat", parseFloat(v) || 0)} testid="edit-lat" />
            <Field label="Longitude" type="number" value={data.lng} onChange={(v) => update("lng", parseFloat(v) || 0)} testid="edit-lng" />
            <Field label="Height (m)" type="number" value={data.height_m} onChange={(v) => update("height_m", parseFloat(v) || 0)} testid="edit-height" />
            <Field label="YouTube URL" value={data.youtube_url} onChange={(v) => update("youtube_url", v)} testid="edit-youtube" wide />
          </Grid>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={!!data.featured}
              onChange={(e) => update("featured", e.target.checked)}
              data-testid="edit-featured"
            />
            Featured on homepage
          </label>
          <label className="flex items-center gap-2 text-sm" data-testid="edit-draft-row">
            <input
              type="checkbox"
              checked={!!data.is_draft}
              onChange={(e) => update("is_draft", e.target.checked)}
              data-testid="edit-draft"
            />
            <span>
              <span className="font-semibold">Draft</span>
              <span className="text-stone-500"> — hidden from visitors, sitemap and Google. Use this while you're still uploading images or writing content.</span>
            </span>
          </label>
        </div>

        <div className="rounded-2xl bg-white border border-stone-200 p-6 space-y-4">
          <h2 className="font-display font-semibold">Content</h2>
          <Area label="Short blurb" value={data.short_blurb} onChange={(v) => update("short_blurb", v)} rows={2} testid="edit-blurb" />
          <Area label="Description" value={data.description} onChange={(v) => update("description", v)} rows={8} testid="edit-description" />
          <Area label="Safety notes (site-specific)" value={data.safety_notes} onChange={(v) => update("safety_notes", v)} rows={5} testid="edit-safety" />
        </div>

        <div className="rounded-2xl bg-white border border-stone-200 p-6 space-y-4">
          <h2 className="font-display font-semibold">SEO meta</h2>
          <Field label="Meta title" value={data.meta_title} onChange={(v) => update("meta_title", v)} testid="edit-metatitle" wide />
          <Area label="Meta description" value={data.meta_description} onChange={(v) => update("meta_description", v)} rows={2} testid="edit-metadesc" />
        </div>

        <div className="rounded-2xl bg-white border border-stone-200 p-6 space-y-4">
          <h2 className="font-display font-semibold">Images</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {(data.image_urls || []).map((u, i) => (
              <div key={u + i} className="relative aspect-[4/3] rounded-lg overflow-hidden border border-stone-200">
                <img src={u} alt="" className="w-full h-full object-cover" />
                <button
                  onClick={() => removeImage(i)}
                  data-testid={`remove-image-${i}`}
                  className="absolute top-1 right-1 bg-white/90 rounded-full p-1 hover:bg-red-100"
                >
                  <Trash2 className="w-4 h-4 text-red-600" />
                </button>
              </div>
            ))}
            <label className="aspect-[4/3] rounded-lg border-2 border-dashed border-stone-300 flex flex-col items-center justify-center text-stone-500 hover:border-[hsl(var(--primary))] cursor-pointer">
              <Upload className="w-6 h-6 mb-1" />
              <span className="text-xs">{uploading ? "Uploading…" : "Upload image"}</span>
              <input type="file" accept="image/*" className="hidden" onChange={uploadImage} data-testid="edit-upload-image" />
            </label>
          </div>
        </div>
      </div>
    </main>
  );
}

function Grid({ children }) {
  return <div className="grid md:grid-cols-2 gap-4">{children}</div>;
}

function Field({ label, value, onChange, type = "text", testid, wide }) {
  return (
    <label className={`block ${wide ? "md:col-span-2" : ""}`}>
      <span className="block text-sm font-medium text-stone-700 mb-1">{label}</span>
      <input
        type={type}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-lg border border-stone-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
      />
    </label>
  );
}

function Area({ label, value, onChange, rows = 4, testid }) {
  return (
    <label className="block">
      <span className="block text-sm font-medium text-stone-700 mb-1">{label}</span>
      <textarea
        rows={rows}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        data-testid={testid}
        className="w-full rounded-lg border border-stone-300 px-3 py-2 outline-none focus:ring-2 focus:ring-[hsl(var(--primary))]"
      />
    </label>
  );
}
